import React from "react";
import {
    useGetAdminNewsCategoriesQuery,
    useCreateAdminNewsCategoryMutation,
    useUpdateAdminNewsCategoryMutation,
    useDeleteAdminNewsCategoryMutation,
} from "../../store/api";

type Props = {
    open: boolean;
    onClose: () => void;
};

const LANGS = ["ru", "en", "ka", "uk"] as const;

const NewsCategoriesModal: React.FC<Props> = ({ open, onClose }) => {
    const { data, isFetching, refetch } = useGetAdminNewsCategoriesQuery(
        undefined,
        { skip: !open }
    );
    const [createCategory, { isLoading: isCreating }] =
        useCreateAdminNewsCategoryMutation();
    const [updateCategory, { isLoading: isUpdating }] =
        useUpdateAdminNewsCategoryMutation();
    const [deleteCategory, { isLoading: isDeleting }] =
        useDeleteAdminNewsCategoryMutation();

    const [editingId, setEditingId] = React.useState<number | null>(null);
    const [slug, setSlug] = React.useState<string>("");
    const [isActive, setIsActive] = React.useState<boolean>(true);
    const [names, setNames] = React.useState<Record<string, string>>({ ru: "" });

    React.useEffect(() => {
        if (!open) {
            setEditingId(null);
            setSlug("");
            setIsActive(true);
            setNames({ ru: "" });
        }
    }, [open]);

    const startEdit = (cat: any) => {
        setEditingId(cat.id);
        setSlug(cat.slug || "");
        setIsActive(!!cat.is_active);
        const map: Record<string, string> = {};
        for (const l of LANGS) {
            map[l] = cat.translations?.[l]?.name || "";
        }
        setNames(map);
    };

    const resetForm = () => {
        setEditingId(null);
        setSlug("");
        setIsActive(true);
        setNames({ ru: "" });
    };

    const submit = async () => {
        const payload = {
            slug: slug || null,
            is_active: isActive,
            translations: Object.fromEntries(
                Object.entries(names).map(([l, name]) => [l, { name }])
            ),
        };
        if (editingId) {
            await updateCategory({ id: editingId, data: payload }).unwrap();
        } else {
            await createCategory(payload).unwrap();
        }
        await refetch();
        resetForm();
    };

    const remove = async (id: number) => {
        if (!window.confirm("Удалить категорию?")) return;
        await deleteCategory(id).unwrap();
        await refetch();
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={onClose}
            />
            <div className="relative bg-white w-full max-w-4xl rounded shadow-lg p-6 z-10">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold">Категории новостей</h2>
                    <button className="p-2" onClick={onClose}>
                        ✕
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <div className="font-semibold mb-2">
                            {editingId ? `Редактирование #${editingId}` : "Новая"}
                        </div>
                        <div className="grid gap-3">
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Slug (опционально)
                                </label>
                                <input
                                    className="w-full border rounded px-3 py-2"
                                    value={slug}
                                    onChange={(e) => setSlug(e.target.value)}
                                />
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                />
                                Активна
                            </label>
                            {LANGS.map((l) => (
                                <div key={l}>
                                    <label className="block text-sm font-medium mb-1">
                                        Название ({l.toUpperCase()})
                                    </label>
                                    <input
                                        className="w-full border rounded px-3 py-2"
                                        value={names[l] || ""}
                                        onChange={(e) =>
                                            setNames((prev) => ({
                                                ...prev,
                                                [l]: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                            ))}
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
                                    onClick={submit}
                                    disabled={isCreating || isUpdating}
                                >
                                    {editingId ? "Сохранить" : "Создать"}
                                </button>
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-gray-100 rounded"
                                    onClick={resetForm}
                                >
                                    Сброс
                                </button>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="font-semibold mb-2">Список</div>
                        {isFetching && (
                            <div className="py-6 text-center text-gray-500">
                                Загрузка...
                            </div>
                        )}
                        {Array.isArray(data) && (
                            <div className="border rounded divide-y">
                                {data.map((c: any) => (
                                    <div
                                        key={c.id}
                                        className="p-3 flex items-center justify-between gap-3"
                                    >
                                        <div className="min-w-0">
                                            <div className="font-medium truncate">
                                                {c.translations?.ru?.name || "—"}{" "}
                                                <span className="text-gray-400">
                                                    ({c.slug})
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {c.is_active ? "active" : "inactive"}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                className="px-3 py-1 rounded bg-gray-100"
                                                onClick={() => startEdit(c)}
                                            >
                                                Ред.
                                            </button>
                                            <button
                                                className="px-3 py-1 rounded bg-red-100 text-red-700 disabled:opacity-50"
                                                onClick={() => remove(c.id)}
                                                disabled={isDeleting}
                                            >
                                                Удал.
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewsCategoriesModal;


